<script setup lang="ts">
// Vue
import { computed, onMounted, onUnmounted, watch, ref } from "vue";

// Components
import ModalHeading from "./ModalHeading.vue";

// ///////////////////
// Props
// ///////////////////

const props = defineProps<{
  heading?: string;
  size: "xl" | "lg" | "md" | "sm" | (string & {});
  height?: string;
  show?: () => boolean;
  persistent?: boolean;
  // Accessible name for dialogs that render their own header instead of passing `heading` —
  // without one of the two, screen readers announce the generic "Modal dialog".
  ariaLabel?: string;
}>();

// ///////////////////
// Refs
// ///////////////////

const modal = ref();
const showModal = ref<boolean>(false);

// ///////////////////
// Emits
// ///////////////////

const emits = defineEmits(["toggleModal"]);

// ///////////////////
// Lifecycle
// ///////////////////

// Module-level counter so nested Modal.vue instances don't prematurely restore scroll lock.
// Only the first open applies the lock; only the last close removes it.
let openModalCount = 0;

// Per-instance flag prevents double-decrement if the watch and onUnmounted both fire.
const isScrollLocked = ref(false);

// The element that was focused when this modal opened, so closing hands focus back instead of
// dropping it on <body> (the app root is inert while a modal is open, so a dropped focus forces
// keyboard users to Tab in from the top). Mirrors Popover's returnFocus.
let previouslyFocusedElement: HTMLElement | null = null;

const restoreFocus = () => {
  if (previouslyFocusedElement && document.contains(previouslyFocusedElement)) {
    previouslyFocusedElement.focus();
  }
  previouslyFocusedElement = null;
};

const lockBodyScroll = () => {
  if (isScrollLocked.value) return;
  isScrollLocked.value = true;
  openModalCount++;
  if (openModalCount === 1) {
    document.body.style.overflow = "hidden";
    document.querySelector("#app")?.setAttribute("inert", "");
  }
};

const restoreBodyScroll = () => {
  if (!isScrollLocked.value) return;
  isScrollLocked.value = false;
  openModalCount--;
  if (openModalCount === 0) {
    document.body.style.overflow = "";
    document.querySelector("#app")?.removeAttribute("inert");
  }
};

// Set the showModal ref to true (or props.show() if using show prop) when the component is mounted
onMounted(() => {
  if (props.show) {
    showModal.value = props.show();
  } else {
    showModal.value = true;
  }
});

onUnmounted(() => {
  restoreBodyScroll();
  restoreFocus();
});

// ///////////////////
// Watchers
// ///////////////////

// Watch for changes in the optional show prop and update the showModal ref accordingly
watch(
  () => props.show && props.show(),
  (newVal) => {
    showModal.value = newVal !== undefined ? newVal : false;
  }
);

// Watch for changes in the showModal ref, manage scroll lock and focus
watch(
  () => showModal.value,
  (newVal) => {
    if (newVal) {
      previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      lockBodyScroll();
      setTimeout(() => {
        if (modal.value) {
          modal.value.focus();
        }
      }, 300);
    } else {
      restoreBodyScroll();
      restoreFocus();
    }
  }
);

const toggleModal = () => {
  if (props.persistent) return;
  showModal.value = false;
  setTimeout(() => {
    emits("toggleModal");
  }, 200);
};

const size = computed(() => {
  if (props.size === "xl") return "h-5/6 w-5/6";
  else if (props.size === "lg") return "h-4/6 w-4/6";
  else if (props.size === "md") return "h-3/6 w-3/6";
  else if (props.size === "sm") return "h-2/6 w-2/6";
  else return props.size;
});
</script>

<template>
  <Teleport to="#modal">
    <Transition
      name="modal"
      appear>
      <div
        v-if="showModal"
        ref="modal"
        role="dialog"
        aria-modal="true"
        :aria-label="heading || ariaLabel || 'Modal dialog'"
        class="z-modal bg-scrim/60 fixed inset-0 flex flex-col items-center backdrop-blur-sm"
        tabindex="0"
        @click="toggleModal"
        @keyup.esc="toggleModal"
        @click.stop>
        <div
          class="modal-container modal-panel rounded-8 bg-elevated shadow-modal relative m-auto flex max-h-[90vh] max-w-[85vw] flex-col ring-1 ring-slate-200 transition-all"
          tabindex="0"
          :class="[heading === undefined ? '' : 'p-px-20', size, height]"
          @keyup.esc="toggleModal"
          @click.stop>
          <ModalHeading
            v-if="heading"
            :label="heading"
            @on-close="toggleModal" />
          <div class="scrollbar pr-px-16 h-full w-full overflow-auto">
            <slot></slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style>
.modal-enter-active,
.modal-leave-active,
.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition:
    opacity 0.3s ease-out,
    transform 0.3s ease-out;
}

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container {
  -webkit-transform: scale(1.05);
  transform: scale(1.05);
}

.modal-leave-to .modal-container {
  -webkit-transform: scale(0.95);
  transform: scale(0.95);
}

.scrollbar::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.scrollbar::-webkit-scrollbar-track {
  border-radius: 100vh;
  background: transparent;
}

.scrollbar::-webkit-scrollbar-thumb {
  border-radius: 100vh;
  background: var(--color-type-muted); /* migrated to token – was #94a3b8 */
}

.scrollbar::-webkit-scrollbar-track-piece:end {
  background: var(--color-slate-100); /* migrated to token – was #f1f5f9 */
  margin-bottom: 0;
  border-radius: 100vh;
}

.scrollbar::-webkit-scrollbar-track-piece:start {
  background: var(--color-slate-100); /* migrated to token – was #f1f5f9 */
  border-radius: 100vh;
  margin-top: 0;
}
</style>
